/**
 * AX1 Navigation Continuity System — Core Types
 *
 * Prompt 2:  Surface Identity Model
 * Prompt 3:  Navigation Context Model
 * Prompt 4:  Origin Envelope / Return Context Model
 * Prompt 5:  Surface Session Lifecycle Model
 * Prompt 8:  Overlay Stack Model
 *
 * @module lib/navigation/types
 */

// ============================================================================
// 1. SURFACE IDENTITY MODEL (Prompt 2)
// ============================================================================

/**
 * NAMING RULES
 * ────────────────────────────────────────────────────────────────────────
 * kind     │ name pattern             │ entityType  │ entityId
 * ─────────┼──────────────────────────┼─────────────┼────────────────────
 * page     │ kebab-case noun          │ optional    │ route param
 *          │ 'projects', 'project-detail', 'settings'
 * tab      │ parent + '-tab' suffix   │ inherits    │ inherits
 *          │ 'milestones-tab', 'capital-tab', 'team-tab'
 * section  │ parent + '-section'      │ inherits    │ inherits
 *          │ 'governance-section', 'access-section'
 * drawer   │ entity + '-drawer'       │ required    │ required
 *          │ 'milestone-drawer', 'capital-manage-drawer', 'gate-detail-drawer'
 * modal    │ action/entity + '-modal' │ optional    │ optional
 *          │ 'evidence-modal', 'delete-confirmation-modal'
 * ────────────────────────────────────────────────────────────────────────
 */
export type SurfaceKind = 'page' | 'tab' | 'section' | 'drawer' | 'modal';

export interface SurfaceId {
    /** Discriminator: what kind of surface this is */
    readonly kind: SurfaceKind;
    /**
     * Stable name scoped to the kind. Always kebab-case.
     * Must be unique within its kind.
     */
    readonly name: string;
    /**
     * Entity type for entity-bound surfaces.
     * Uses AX1 domain nouns: 'project', 'milestone', 'gate', 'task',
     * 'settlement', 'user', 'role-policy', 'signature-request', 'evidence'.
     */
    readonly entityType?: string;
    /**
     * Entity id — the runtime id of the specific entity.
     * Never an array index. Must be a stable database/domain id.
     */
    readonly entityId?: string;
}

/** Deterministic string key derived from a SurfaceId */
export type SurfaceKey = string;

/** Compute a deterministic key from a SurfaceId */
export function surfaceKey(s: SurfaceId): SurfaceKey {
    const parts = [s.kind, s.name];
    if (s.entityType) parts.push(s.entityType);
    if (s.entityId) parts.push(s.entityId);
    return parts.join(':');
}

// ── Predefined Surface Factories ──────────────────────────────────────────

export const Surfaces = {
    // Pages
    projectDetail: (projectId: string): SurfaceId => ({
        kind: 'page', name: 'project-detail', entityType: 'project', entityId: projectId,
    }),
    projects: (): SurfaceId => ({ kind: 'page', name: 'projects' }),
    milestones: (): SurfaceId => ({ kind: 'page', name: 'milestones' }),
    documents: (): SurfaceId => ({ kind: 'page', name: 'documents' }),
    users: (): SurfaceId => ({ kind: 'page', name: 'users' }),
    tasks: (): SurfaceId => ({ kind: 'page', name: 'tasks' }),
    companies: (): SurfaceId => ({ kind: 'page', name: 'companies' }),
    settings: (): SurfaceId => ({ kind: 'page', name: 'settings' }),
    analytics: (): SurfaceId => ({ kind: 'page', name: 'analytics' }),
    finance: (): SurfaceId => ({ kind: 'page', name: 'finance' }),

    // Tabs (scoped to parent page)
    milestonesTab: (projectId: string): SurfaceId => ({
        kind: 'tab', name: 'milestones-tab', entityType: 'project', entityId: projectId,
    }),
    capitalTab: (projectId: string): SurfaceId => ({
        kind: 'tab', name: 'capital-tab', entityType: 'project', entityId: projectId,
    }),
    teamTab: (projectId: string): SurfaceId => ({
        kind: 'tab', name: 'team-tab', entityType: 'project', entityId: projectId,
    }),
    tasksTab: (projectId: string): SurfaceId => ({
        kind: 'tab', name: 'tasks-tab', entityType: 'project', entityId: projectId,
    }),
    documentsTab: (projectId: string): SurfaceId => ({
        kind: 'tab', name: 'documents-tab', entityType: 'project', entityId: projectId,
    }),

    // Drawers
    milestoneDrawer: (milestoneId: string): SurfaceId => ({
        kind: 'drawer', name: 'milestone-drawer', entityType: 'milestone', entityId: milestoneId,
    }),
    capitalManageDrawer: (milestoneId: string): SurfaceId => ({
        kind: 'drawer', name: 'capital-manage-drawer', entityType: 'milestone', entityId: milestoneId,
    }),
    gateDetailDrawer: (gateId: string): SurfaceId => ({
        kind: 'drawer', name: 'gate-detail-drawer', entityType: 'gate', entityId: gateId,
    }),
    createProjectDrawer: (): SurfaceId => ({
        kind: 'drawer', name: 'create-project-drawer',
    }),
    createMilestoneDrawer: (): SurfaceId => ({
        kind: 'drawer', name: 'create-milestone-drawer',
    }),
    // Document surfaces
    documentInspector: (documentId: string): SurfaceId => ({
        kind: 'drawer', name: 'document-inspector', entityType: 'document', entityId: documentId,
    }),
    documentActions: (documentId: string): SurfaceId => ({
        kind: 'drawer', name: 'document-actions-drawer', entityType: 'document', entityId: documentId,
    }),
    documentShare: (documentId: string): SurfaceId => ({
        kind: 'modal', name: 'document-share-modal', entityType: 'document', entityId: documentId,
    }),
    documentPreview: (documentId: string): SurfaceId => ({
        kind: 'modal', name: 'document-preview-modal', entityType: 'document', entityId: documentId,
    }),
    documentFilters: (): SurfaceId => ({
        kind: 'drawer', name: 'document-filters-drawer',
    }),
    documentNewFolder: (): SurfaceId => ({
        kind: 'modal', name: 'document-new-folder-modal',
    }),
    documentUpload: (): SurfaceId => ({
        kind: 'modal', name: 'document-upload-modal',
    }),
    // Admin surfaces
    companyDetail: (companyId: string): SurfaceId => ({
        kind: 'drawer', name: 'company-detail-drawer', entityType: 'company', entityId: companyId,
    }),
    // Task surfaces
    taskInspector: (taskId: string): SurfaceId => ({
        kind: 'drawer', name: 'task-inspector-drawer', entityType: 'task', entityId: taskId,
    }),
    createTaskDrawer: (projectId: string): SurfaceId => ({
        kind: 'drawer', name: 'create-task-drawer', entityType: 'project', entityId: projectId,
    }),
    teamManagementDrawer: (projectId: string): SurfaceId => ({
        kind: 'drawer', name: 'team-management-drawer', entityType: 'project', entityId: projectId,
    }),
    userInspectorDrawer: (userId: string): SurfaceId => ({
        kind: 'drawer', name: 'user-inspector-drawer', entityType: 'user', entityId: userId,
    }),
    settlementDetailDrawer: (settlementId: string): SurfaceId => ({
        kind: 'drawer', name: 'settlement-detail-drawer', entityType: 'settlement', entityId: settlementId,
    }),

    // Modals
    evidenceModal: (evidenceId: string): SurfaceId => ({
        kind: 'modal', name: 'evidence-modal', entityType: 'evidence', entityId: evidenceId,
    }),
    deleteConfirmationModal: (entityType?: string, entityId?: string): SurfaceId => ({
        kind: 'modal', name: 'delete-confirmation-modal', entityType, entityId,
    }),
    shareEvidencePackModal: (packId: string): SurfaceId => ({
        kind: 'modal', name: 'share-evidence-pack-modal', entityType: 'evidence-pack', entityId: packId,
    }),

    // Capital — payment request panel
    paymentRequestPanel: (requestId: string): SurfaceId => ({
        kind: 'drawer', name: 'payment-request-panel', entityType: 'payment-request', entityId: requestId,
    }),
} as const;

// ============================================================================
// 2. NAVIGATION CONTEXT (Prompt 3)
// ============================================================================

/**
 * Snapshot of the user's position within a surface.
 * Captured when leaving a surface and restored on return.
 *
 * Field-by-field:
 *   surface         – which surface the user is on
 *   pathname        – React Router pathname at capture time
 *   search          – URL search string including '?'
 *   hash            – URL hash including '#'
 *   activeTab       – current tab key if the surface has tabs
 *   activeSection   – current section key in a StructuredDrawerShell
 *   scrollTop       – pixel offset of the main scrollable region
 *   focusedEntityId – DOM id / data-highlight-id of the focused/selected entity
 *   selectedEntityId– logical entity id of a selected row/card (may differ from DOM id)
 *   triggerElementId– DOM id of the element that the user clicked to open a child
 *   capturedAt      – Date.now() when the snapshot was taken
 */
export interface NavigationContext {
    readonly surface: SurfaceId;
    readonly pathname: string;
    readonly search: string;
    readonly hash: string;
    readonly activeTab?: string;
    readonly activeSection?: string;
    readonly scrollTop?: number;
    readonly focusedEntityId?: string;
    readonly selectedEntityId?: string;
    readonly triggerElementId?: string;
    readonly capturedAt: number;
}

// ============================================================================
// 3. ORIGIN ENVELOPE / RETURN CONTEXT (Prompt 4)
// ============================================================================

/**
 * Return policy determines what happens when a child overlay closes.
 *
 *   restore-origin    – return to the parent surface, restore its context,
 *                       scroll to and highlight the trigger element.
 *   close-to-parent   – close this overlay and give focus back to the
 *                       immediate parent overlay (nested drawers).
 *   redirect-canonical– no live parent exists; navigate to the canonical
 *                       host route resolved from CanonicalHostRegistry.
 */
export type ReturnPolicy = 'restore-origin' | 'close-to-parent' | 'redirect-canonical';

/**
 * Packaged by the parent surface when opening a child overlay.
 * Carries everything needed for deterministic return behavior.
 */
export interface OriginEnvelope {
    /** The parent surface that opened the child */
    readonly origin: SurfaceId;
    /** Full navigation context snapshot of the parent at open-time */
    readonly context: NavigationContext;
    /**
     * DOM identifier (id or data-highlight-id) of the trigger element.
     * Used to scroll-to and highlight on return.
     */
    readonly triggerElementId?: string;
    /**
     * Preferred return policy. Orchestrator may override if the parent is gone.
     * Default: 'restore-origin'.
     */
    readonly returnPolicy?: ReturnPolicy;
    /**
     * Optional: parent overlay session instanceId (for nested overlay stacks).
     * When present, close-to-parent pops to this specific stack entry.
     */
    readonly parentSessionId?: string;
    /**
     * Canonical host fallback info — pre-resolved so the close path doesn't
     * need to look anything up. Populated by OverlayOrchestrator at open-time
     * if the host registry has an entry.
     */
    readonly canonicalFallback?: {
        readonly route: string;
        readonly params?: Record<string, string>;
    };
    /**
     * Mutable action result passed back from child → parent on close.
     * E.g. { action: 'saved', entityId: 'ms-123' }
     */
    returnPayload?: Record<string, unknown>;
}

// ============================================================================
// 4. OVERLAY STACK MODEL (Prompt 8)
// ============================================================================

export type OverlayKind = 'drawer' | 'modal';

/**
 * Surface session lifecycle state.
 *
 *   opening  – open requested, entry animation running
 *   open     – fully visible, interactive
 *   closing  – close requested, exit animation running
 *   closed   – removed from stack, cleanup complete
 *
 * Transitions:
 *   → opening → open → closing → closed
 *
 * Rules:
 *   - openOverlay sets lifecycle to 'opening'; the React component
 *     calls markOpen() after onAnimationComplete.
 *   - closeOverlay sets lifecycle to 'closing'; removal from stack
 *     happens after exit animation via markClosed().
 *   - Idempotent: calling openOverlay while lifecycle is 'opening'
 *     or 'open' is a no-op (returns existing entry).
 */
export type OverlayLifecycle = 'opening' | 'open' | 'closing' | 'closed';

export interface OverlayEntry {
    /** Unique instance id — never reused */
    readonly instanceId: string;
    /** Surface identity of this overlay */
    readonly surface: SurfaceId;
    /** Drawer or modal */
    readonly overlayKind: OverlayKind;
    /** Origin envelope from the parent */
    readonly origin: OriginEnvelope;
    /** z-index tier (auto-assigned by orchestrator) */
    readonly zIndex: number;
    /** Backdrop z-index (zIndex - 5) */
    readonly backdropZIndex: number;
    /** Props to forward to the overlay component */
    readonly props: Record<string, unknown>;
    /** Timestamp when the overlay was opened */
    readonly openedAt: number;
    /** Current lifecycle state */
    lifecycle: OverlayLifecycle;
}

// ============================================================================
// 5. SURFACE SESSION — ephemeral page/tab state (Prompt 5)
// ============================================================================

/**
 * Serialisable page/tab state that survives in-app navigation but not
 * browser close. Stored in sessionStorage (30min TTL).
 *
 * URL-synced state (tab, filter via useURLState/useListPageState) is the
 * source of truth for durable state. SurfaceSession handles ephemeral
 * state that is too noisy for URLs.
 */
export interface SurfaceSession {
    /** Surface key this session belongs to */
    readonly surfaceKey: SurfaceKey;
    /** Active tab */
    activeTab?: string;
    /** Active section */
    activeSection?: string;
    /** Scroll offset */
    scrollTop?: number;
    /** Filter/sort/pagination state — serialisable record */
    listState?: Record<string, string>;
    /** Selected entity id */
    selectedEntityId?: string;
    /** View mode (e.g. 'grid' | 'list') */
    viewMode?: string;
    /** Timestamp of last update */
    updatedAt: number;
}

// ============================================================================
// 6. CANONICAL HOST ENTRY (Prompt 9)
// ============================================================================

/**
 * Registered canonical host for a surface kind/name combination.
 * Used as fallback return destination when no live parent exists.
 */
export interface CanonicalHostEntry {
    /** The overlay surface that this host receives */
    readonly overlaySurface: Pick<SurfaceId, 'kind' | 'name'>;
    /** The host page route (may contain :param placeholders) */
    readonly hostRoute: string;
    /** Search params to append when navigating to the host */
    readonly hostParams?: Record<string, string>;
    /** Surface id of the host (for context restoration) */
    readonly hostSurface?: SurfaceId;
    /** data-highlight-id pattern for the trigger on the host page */
    readonly highlightPattern?: string;
}

// ============================================================================
// 7. EVENT TYPES (overlay lifecycle)
// ============================================================================

export type OverlayEvent =
    | { type: 'opened'; entry: OverlayEntry }
    | { type: 'lifecycle'; instanceId: string; lifecycle: OverlayLifecycle }
    | { type: 'closed'; instanceId: string; returnPayload?: Record<string, unknown>; cascade?: boolean }
    | { type: 'focusChanged'; instanceId: string };

export type OverlayEventHandler = (event: OverlayEvent) => void;

// ============================================================================
// 8. DOM IDENTITY CONVENTION (Prompt 11)
// ============================================================================

/**
 * Stable DOM identity helpers.
 *
 * Convention:
 *   data-entity-id="{entityType}-{entityId}"
 *   data-highlight-id="{entityType}-{entityId}"  (same value for compat)
 *
 * Examples:
 *   milestone-ms-12       — milestone card
 *   gate-g-44             — capital gate card
 *   task-t-99             — task row
 *   settlement-s-88       — settlement row
 *   project-p-7           — project card
 *   user-u-3              — user row
 *
 * Rules:
 *   1. Never use array index as id.
 *   2. Place on the outermost interactive wrapper, not on inner children.
 *   3. Cards, rows, and list items MUST carry both attributes.
 *   4. Trigger buttons that open overlays SHOULD carry the attribute
 *      with a verb prefix: 'open-milestone-drawer-ms-12'.
 *   5. Tab buttons use: 'tab-{tabKey}'.
 *   6. Section anchors use: 'section-{sectionKey}'.
 */

/** Build a standard entity DOM id */
export function entityDomId(entityType: string, entityId: string): string {
    return `${entityType}-${entityId}`;
}

/** Props object to spread onto a list item/card/row element */
export function entityIdAttrs(entityType: string, entityId: string) {
    const id = entityDomId(entityType, entityId);
    return {
        'data-entity-id': id,
        'data-highlight-id': id,
    } as const;
}

/** Build a trigger button DOM id for overlay open buttons */
export function triggerDomId(action: string, entityType: string, entityId: string): string {
    return `${action}-${entityType}-${entityId}`;
}
